import { Controller, useFieldArray } from 'react-hook-form'
import { InputText } from '@/components/commons/input-text'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon, PlusIcon, Trash01Icon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/translations/i18n'
import { useChangeStep } from '../hooks/use-change-step'
import { useExperienceShellFormContext } from '../hooks/use-experience-shell-form-context'
import { StepKeyName } from '../types'
import { ColumnFields, Content, Fieldset, Footer, YesNoField } from './atoms'
import { CountriesSelect } from './countries-select'

export const BackgroundStep = ({
	experienceId,
	isUpgrade
}: {
	experienceId: number
	isUpgrade: boolean
}) => {
	const { t } = useTranslation()
	const {
		control,
		register,
		formState: { errors }
	} = useExperienceShellFormContext(isUpgrade)
	const changeStep = useChangeStep({
		experienceId,
		currentStep: StepKeyName.enum.background,
		isUpgrade
	})
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'background.online_profile_links'
	})

	return (
		<>
			<Content title={t('experience_shell:step_background')}>
				<div className='flex flex-col gap-6'>
					<Fieldset title={t('experience_shell:expertise')}>
						<InputText
							errorMessage={
								errors.background?.expertise_years
									? t('errors:integer_error')
									: undefined
							}
							inputSize='sm'
							label={t('experience_shell:years_experience')}
							placeholder={t(
								'experience_shell:enter_number_years'
							)}
							type='text'
							{...register('background.expertise_years')}
						/>
					</Fieldset>
					<Separator orientation='horizontal' />
					<Fieldset title={t('experience_shell:online_profiles')}>
						{fields.map((field, index) => (
							<div
								className='flex w-full flex-col gap-2'
								key={field.id}
							>
								<div className='grid w-full grid-cols-[1fr_auto] items-end gap-4'>
									<InputText
										inputSize='sm'
										label={t(
											'experience_shell:online_profile'
										)}
										placeholder={t(
											'experience_shell:enter_link'
										)}
										type='text'
										{...register(
											`background.online_profile_links.${index}.link`
										)}
									/>
									{fields.length >= 2 && (
										<ButtonLegacy
											onClick={() => remove(index)}
											shape='square'
											type='button'
											variant='tertiary'
										>
											<Trash01Icon />
										</ButtonLegacy>
									)}
								</div>
								{errors.background?.online_profile_links?.[
									index
								]?.link && (
									<InputHelpText
										error={t(
											'experience_shell:online_profile_error'
										)}
									/>
								)}
							</div>
						))}
						<ButtonLegacy
							onClick={() => append({ link: '' })}
							size='sm'
							type='button'
							variant='tertiary'
						>
							<PlusIcon />
							{t('experience_shell:add_profile')}
						</ButtonLegacy>
					</Fieldset>
					<Separator orientation='horizontal' />
					<Fieldset title={t('experience_shell:residential_address')}>
						<Controller
							control={control}
							name='background.residential_country_region'
							render={({ field }) => (
								<CountriesSelect
									errorMessage={
										errors.background
											?.residential_country_region
											? t('errors:field_required')
											: undefined
									}
									onChange={field.onChange}
									value={field.value ?? ''}
								/>
							)}
						/>
						<InputText
							errorMessage={
								errors.background?.residential_address
									? t('errors:field_required')
									: undefined
							}
							inputSize='sm'
							label={t('experience_shell:street_address')}
							type='text'
							{...register('background.residential_address')}
						/>
						<InputText
							inputSize='sm'
							label={t('experience_shell:apt_suite')}
							type='text'
							{...register(
								'background.residential_address_extras'
							)}
						/>
						<InputText
							errorMessage={
								errors.background?.residential_city
									? t('errors:field_required')
									: undefined
							}
							inputSize='sm'
							label={t('experience_shell:city')}
							type='text'
							{...register('background.residential_city')}
						/>
						<ColumnFields>
							<InputText
								errorMessage={
									errors.background?.residential_state
										? t('errors:field_required')
										: undefined
								}
								inputSize='sm'
								label={t('experience_shell:state_territory')}
								type='text'
								{...register('background.residential_state')}
							/>
							<InputText
								errorMessage={
									errors.background?.residential_zip
										? t('errors:field_required')
										: undefined
								}
								inputSize='sm'
								label={t('experience_shell:zip_code')}
								type='text'
								{...register('background.residential_zip')}
							/>
						</ColumnFields>
					</Fieldset>
					<Fieldset>
						<Controller
							control={control}
							name='background.hosting_as_business'
							render={({ field }) => (
								<div className='flex w-full flex-col gap-2'>
									<YesNoField
										labelTitle={
											<b>
												{t(
													'experience_shell:hosting_as_business'
												)}
											</b>
										}
										name={field.name}
										onChange={(value) =>
											field.onChange(value)
										}
										value={field.value}
									/>
									{errors.background?.hosting_as_business && (
										<InputHelpText
											error={t('errors:field_required')}
										/>
									)}
								</div>
							)}
						/>
					</Fieldset>
				</div>
			</Content>
			<Footer>
				<ButtonLegacy
					disabled={
						changeStep.isBackLoading || changeStep.isContinueLoading
					}
					onClick={() =>
						changeStep.handleChangeStep({
							isBack: true,
							stepToGoTo: StepKeyName.enum.experience
						})
					}
					type='button'
					variant='tertiary'
				>
					{t('common:back')}
					{changeStep.isBackLoading && <Spinner />}
				</ButtonLegacy>
				<ButtonLegacy
					disabled={
						changeStep.isContinueLoading || changeStep.isBackLoading
					}
					onClick={() =>
						changeStep.handleChangeStep({
							stepToGoTo: StepKeyName.enum.merchandising
						})
					}
					type='button'
				>
					{t('common:continue')}
					{changeStep.isContinueLoading ? (
						<Spinner />
					) : (
						<ArrowRightIcon />
					)}
				</ButtonLegacy>
			</Footer>
		</>
	)
}
